import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";

export default function Navigation() {
  const { t, lang, toggle } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const NAV = [
    { label: t("nav.canais"), href: "#canais" },
    { label: t("nav.trabalho"), href: "#trabalho" },
    { label: t("nav.sobre"), href: "#sobre" },
    { label: t("nav.marcas"), href: "#marcas" },
    { label: t("nav.servicos"), href: "#servicos" },
    { label: t("nav.parcerias"), href: "/brand-partnership-kit", route: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-[rgba(4,8,7,0.7)] backdrop-blur-xl border-b border-[var(--tt-border)]"
          : "py-6 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="group flex items-center gap-3"
        >
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_brand-portfolio-41/artifacts/6ixqaeld_a148c716-4aaf-4746-83a8-203a7ec4bc9a.png"
            alt="Terra e Trilhos"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif italic text-[18px] tracking-tight text-[var(--tt-text)]">
              Terra <span className="not-italic text-[var(--tt-text-muted)]">e</span> Trilhos
            </span>
            <span className="tt-overline mt-1 text-[10px]">{t("nav.brand_overline")}</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => {
            const testid = `nav-${item.href.replace(/[#/]/g, "").replace(/[^a-z0-9-]/gi, "-").toLowerCase()}`;
            const cls =
              "tt-link-underline text-[13px] tracking-[0.2em] uppercase font-medium text-[var(--tt-text-dim)] hover:text-[var(--tt-text)]";
            return item.route ? (
              <Link key={item.href} to={item.href} data-testid={testid} className={cls}>
                {item.label}
              </Link>
            ) : (
              <a key={item.href} href={item.href} data-testid={testid} className={cls}>
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangToggle lang={lang} toggle={toggle} />
          <a
            href="#contacto"
            data-testid="header-cta"
            className="tt-btn tt-btn-primary"
          >
            {t("nav.cta")}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LangToggle lang={lang} toggle={toggle} compact />
          <button
            data-testid="mobile-menu-toggle"
            aria-label="menu"
            onClick={() => setOpen((o) => !o)}
            className="w-10 h-10 grid place-items-center rounded-full border border-[var(--tt-border-strong)]"
          >
            <span className="block w-4 h-px bg-[var(--tt-text)] relative">
              <span className={`absolute left-0 -top-1.5 w-4 h-px bg-[var(--tt-text)] transition-transform ${open ? "translate-y-1.5 rotate-45" : ""}`} />
              <span className={`absolute left-0 top-1.5 w-4 h-px bg-[var(--tt-text)] transition-transform ${open ? "-translate-y-1.5 -rotate-45" : ""}`} />
            </span>
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden mt-4 mx-6 rounded-2xl tt-glass p-6 flex flex-col gap-5"
        >
          {NAV.map((item) => {
            const testid = `mobile-nav-${item.href.replace(/[#/]/g, "").replace(/[^a-z0-9-]/gi, "-").toLowerCase()}`;
            const cls = "text-[var(--tt-text-dim)] uppercase text-sm tracking-[0.2em]";
            return item.route ? (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                data-testid={testid}
                className={cls}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={testid}
                className={cls}
              >
                {item.label}
              </a>
            );
          })}
          <a
            href="#contacto"
            onClick={() => setOpen(false)}
            className="tt-btn tt-btn-primary justify-center mt-2"
          >
            {t("nav.cta")}
          </a>
        </div>
      )}
    </header>
  );
}

function LangToggle({ lang, toggle, compact = false }) {
  return (
    <button
      data-testid="lang-toggle"
      aria-label="toggle language"
      onClick={toggle}
      className={`relative inline-flex items-center rounded-full border border-[var(--tt-border-strong)] bg-[rgba(230,223,211,0.04)] hover:bg-[rgba(230,223,211,0.08)] transition-colors ${
        compact ? "h-9" : "h-10"
      } px-1`}
    >
      <span
        className={`absolute top-1 bottom-1 w-[42%] rounded-full transition-transform duration-300 ease-out ${
          lang === "en" ? "translate-x-[110%]" : "translate-x-1"
        }`}
        style={{ background: "var(--tt-gold)" }}
      />
      {["pt", "en"].map((l) => (
        <span
          key={l}
          data-testid={`lang-${l}`}
          className={`relative z-10 px-3 text-[11px] font-medium uppercase tracking-[0.22em] transition-colors ${
            lang === l ? "text-black" : "text-[var(--tt-text-dim)]"
          }`}
        >
          {l}
        </span>
      ))}
    </button>
  );
}
